import React, {FC, useState} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import FormPanel from '../../../components/FormPanel';
import ensureCsrfCookie from '../../../utils/ensureCsrfCookie';
import GradientHeading from '../components/GradientHeading';
import Page from '../components/Page';
import Delay from 'components/Delay';
import {
	Alert,
	AlertIcon,
	AlertTitle,
	Button,
	Center,
	FormControl,
	FormLabel,
	Text,
	Input,
	Spinner,
	Checkbox,
	HStack,
	PinInput,
	PinInputField,
	SlideFade,
	Stack,
} from 'components/ui/chakra';
import LoginController from 'puddleglum/api/auth/LoginController';
import UserController from 'puddleglum/api/UserController';

const LoginForm: FC = () => {
	const [searchParams] = useSearchParams();
	const [currentStep, setCurrentStep] = useState<'login' | 'resetPassword' | 'verifyCode'>(
		'login',
	);
	const [email, setEmail] = useState<string>();
	const [password, setPassword] = useState<string>();
	const [rememberMe, setRememberMe] = useState<number>(0);
	const [userId, setUserId] = useState<number>();
	const [code, setCode] = useState<string>();
	const [errorText, setErrorText] = useState<string>();
	const [isLoading, setIsLoading] = useState(false);
	const [needsNewPassword, setNeedsNewPassword] = useState(false);
	const [newPassword, setNewPassword] = useState<string>();
	const [confirmNewPassword, setConfirmNewPassword] = useState<string>();
	const [passwordResetError, setPasswordResetError] = useState<string>();
	const [recipientsFor2fa, setRecipientsFor2fa] = useState<string[]>();
	const nextPath = searchParams.get('next');

	const getRedirectTarget = (): string => {
		if (!nextPath) return '/';

		try {
			const nextUrl = new URL(nextPath, window.location.origin);
			if (nextUrl.origin !== window.location.origin) {
				return '/';
			}

			return `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
		} catch {
			if (nextPath.startsWith('/') && !nextPath.startsWith('//')) {
				return nextPath;
			}

			return '/';
		}
	};

	const loginUser = async (isResending: boolean = false) => {
		try {
			setErrorText(undefined);
			setIsLoading(true);

			await ensureCsrfCookie();

			const deviceToken = localStorage.getItem('remember_device_token');
			const response = await LoginController.login({
				email: email ?? '',
				password: password ?? '',
				remember_device_token: deviceToken,
				is_resending: isResending,
			});

			if (response.data.message === 'Login successful') {
				window.location.href = getRedirectTarget();
				return;
			}

			setUserId(response.data.user.id);
			setRecipientsFor2fa(response.data.recipients);
			setCurrentStep('verifyCode');
			if (response.data.user.is_temporary_password) {
				setNeedsNewPassword(true);
			}
			setIsLoading(false);
		} catch (error: any) {
			const message =
				error?.response?.data?.message ?? 'Unable to submit login request right now.';
			setErrorText(`An error has occurred: ${message}`);
			setUserId(undefined);
			setIsLoading(false);
		}
	};

	const confirmCode = async () => {
		try {
			if (!userId || !code) {
				throw new Error('Oops! Something went wrong.');
			}

			setErrorText(undefined);
			setIsLoading(true);
			await ensureCsrfCookie();
			const reply = await LoginController.verifyLogin(userId, {
				code,
				remember: rememberMe,
			});

			// Store the remember token in local storage, to be used on subsequent logins
			if (reply.data.remember_device_token)
				localStorage.setItem('remember_device_token', reply.data.remember_device_token);

			if (needsNewPassword) {
				setCurrentStep('resetPassword');
			} else {
				window.location.href = getRedirectTarget();
				return;
			}

			setIsLoading(false);
		} catch (error: any) {
			const message =
				error?.response?.data?.message ??
				'Unable to verify your code right now. Please try again.';
			setErrorText(message);
			setIsLoading(false);
		}
	};

	const resetPassword = async () => {
		if (newPassword !== confirmNewPassword) {
			setPasswordResetError('Passwords do not match');
			return;
		}
		try {
			setIsLoading(true);
			await ensureCsrfCookie();
			await UserController.changeTemporaryPassword({
				password: newPassword ?? '',
			});

			window.location.href = getRedirectTarget();
			return;
		} catch (error) {
			setPasswordResetError('An error occurred while resetting your password');
			setIsLoading(false);
		}
	};

	return (
		<Page>
			<Center w='100%' h='100vh'>
				<FormPanel
					description='Sign in to access your Allora portal with the same calmer visual language as the public site.'
					w={{base: '90%', md: '50%', lg: '40%'}}
				>
					<Center style={isLoading ? {} : {display: 'none'}} w='100%' h='20vh'>
						<Spinner size='xl' />
					</Center>
					<form
						style={isLoading ? {display: 'none'} : {}}
						onSubmit={(e) => {
							e.preventDefault();
						}}
					>
						{currentStep === 'login' && (
							<>
								<GradientHeading>Log In</GradientHeading>
								{errorText && (
									<Alert status='error'>
										<AlertIcon />
										<AlertTitle>{errorText}</AlertTitle>
									</Alert>
								)}

								<FormControl mt={2} id='email' isRequired>
									<FormLabel>Email Address</FormLabel>
									<Input
										key='email'
										name='email'
										type='email'
										value={email}
										onChange={(e) => setEmail(e.target.value)}
									/>
								</FormControl>

								<FormControl mt={2} id='password' isRequired>
									<FormLabel>Password</FormLabel>
									<Input
										key='password'
										name='password'
										type='password'
										value={password}
										onChange={(e) => setPassword(e.target.value)}
									/>
								</FormControl>

								<Link to='/forgot-password'>
									<Text color='primary.500' fontSize='xs' mt={2}>
										Forgot Password?
									</Text>
								</Link>

								<Button
									type='submit'
									mt={4}
									colorScheme='blue'
									w='full'
									onClick={() => loginUser(false)}
									isLoading={isLoading}
								>
									Login
								</Button>
								<Button as={Link} to='/' mt={2} variant='outline' w='full'>
									Back
								</Button>
							</>
						)}

						{currentStep === 'verifyCode' && (
							<>
								<GradientHeading>Verify Your Login</GradientHeading>
								{errorText && (
									<Alert status='error' mt={2}>
										<AlertIcon />
										<AlertTitle>{errorText}</AlertTitle>
									</Alert>
								)}

								<Text mt={4} color='gray.600'>
									{(recipientsFor2fa ?? []).length > 1
										? 'We sent a verification code to:'
										: 'We sent a verification code to'}
								</Text>
								<Stack spacing={1} mt={1} mb={6}>
									{(recipientsFor2fa ?? []).map((recipient) => (
										<Text key={recipient} fontWeight='600'>
											{recipient}
										</Text>
									))}
								</Stack>

								<FormControl id='code'>
									<HStack justify='center' gap={2}>
										<PinInput otp onChange={setCode}>
											<PinInputField />
											<PinInputField />
											<PinInputField />
											<PinInputField />
											<PinInputField />
											<PinInputField />
										</PinInput>
									</HStack>
								</FormControl>

								<Center mt={6}>
									<Checkbox
										colorScheme='teal'
										value={rememberMe}
										onChange={(e) => setRememberMe(e.target.checked ? 1 : 0)}
									>
										Remember this device for 30 days
									</Checkbox>
								</Center>

								<Button
									type='submit'
									mt={6}
									colorScheme='blue'
									w='full'
									onClick={confirmCode}
									isLoading={isLoading}
								>
									Confirm Code
								</Button>
								<Delay seconds={5}>
									<SlideFade in>
										<Button
											mt={2}
											variant='ghost'
											colorScheme='blue'
											w='full'
											onClick={() => loginUser(true)}
											isLoading={isLoading}
										>
											Send Another Code
										</Button>
									</SlideFade>
								</Delay>
							</>
						)}

						{currentStep === 'resetPassword' && (
							<>
								<GradientHeading>Reset Password</GradientHeading>
								{passwordResetError && (
									<Alert status='error'>
										<AlertIcon />
										<AlertTitle>{passwordResetError}</AlertTitle>
									</Alert>
								)}
								<FormControl mt={2} id='new-password' isRequired>
									<FormLabel>New Password</FormLabel>
									<Input
										name='new-password'
										type='password'
										value={newPassword}
										onChange={(e) => setNewPassword(e.target.value)}
									/>
								</FormControl>

								<FormControl mt={2} id='confirm-new-password' isRequired>
									<FormLabel>Confirm New Password</FormLabel>
									<Input
										name='confirm-new-password'
										type='password'
										value={confirmNewPassword}
										onChange={(e) => setConfirmNewPassword(e.target.value)}
									/>
								</FormControl>

								<Button
									type='submit'
									mt={4}
									colorScheme='blue'
									w='full'
									isLoading={isLoading}
									onClick={resetPassword}
								>
									Reset Password
								</Button>
							</>
						)}
					</form>
				</FormPanel>
			</Center>
		</Page>
	);
};

export default LoginForm;
